import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { actions } from 'modules/user';
import classNames from 'classnames';
import * as styles from 'styles/index.scss';
import { Button, Container, Row, Col } from 'reactstrap';
import { faList } from '@fortawesome/pro-regular-svg-icons/faList';
import Disclaimer from '../../routes/Products/products/PPTv2/components/Disclaimer/index';
import { NavItemRow } from './components';

const { makeSideBarVisible, productSideBarContainer, productSidebarChildren, showMoreNav, pptStyles } = styles;

const MOBILE_BREAKPOINT = 768;
const COMPACT_BREAKPOINT = 576;
const MORE_TRIGGER_INDEX = 3;

const getNavItemKey = (item, index, prefix) => `${prefix}-${item.slug || item.name || index}`;

const useWindowWidth = () => {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};

const SideBar = ({
  children = null,
  contentId = null,
  disclaimerRequired = false,
  handleNav,
  navItems = [],
  specialStyles: specialStylesProp = '',
  hideNav
}) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.user);
  const windowWidth = useWindowWidth();
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showMoreNavOpen, setShowMoreNavOpen] = useState(false);

  const path = pathname.split('/');
  const thisProductInPath = path[2] || '';
  const slug = path[3] || '';

  useEffect(() => {
    if (user.isAuthenticated) {
      dispatch(actions.getUserProducts());
    }
  }, [dispatch, user.isAuthenticated]);

  useEffect(() => {
    let isMounted = true;

    if (!contentId) {
      setShowDisclaimer(true);
      return () => {
        isMounted = false;
      };
    }

    dispatch(actions.getUserData(contentId))
      .then(userState => {
        if (!isMounted) return;
        setShowDisclaimer(userState ? userState.showDisclaimer : true);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [contentId, dispatch]);

  const isMobile = windowWidth < MOBILE_BREAKPOINT;

  const visibilityClass =
    showMoreNavOpen || hideNav
      ? 'hideThis'
      : windowWidth > COMPACT_BREAKPOINT
        ? makeSideBarVisible
        : 'd-flex justify-content-around';

  const showMoreNavClass = showMoreNavOpen ? 'makeVisible' : '';

  const handleMore = useCallback(() => {
    setShowMoreNavOpen(prev => !prev);
  }, []);

  const handleItemClick = useCallback(
    item => {
      if (item.inactive) return;
      if (showDisclaimer && disclaimerRequired) return;

      setShowMoreNavOpen(false);
      handleNav(item.name, item.typeOfComponent, item.slug);
    },
    [disclaimerRequired, handleNav, showDisclaimer]
  );

  const handleViewAllProducts = useCallback(() => {
    handleNav('', 'InternalLink', '/products/');
  }, [handleNav]);

  const handleDisclaimer = useCallback(() => {
    setShowDisclaimer(false);
    window.scrollTo(0, 0);

    dispatch(actions.postUserData(contentId, { showDisclaimer: false })).catch(() => {});
  }, [contentId, dispatch]);

  const renderViewAllButton = (buttonProps = {}) => {
    const { wrapperClassName, ...rest } = buttonProps;
    return (
      <div className={classNames('my-3', wrapperClassName)}>
        <Button onClick={handleViewAllProducts} color="primary" {...rest}>
          View All My Products
        </Button>
      </div>
    );
  };

  const renderExpandedNavItem = useCallback(
    (item, index) => (
      <NavItemRow
        key={getNavItemKey(item, index, 'show-more')}
        item={item}
        currentSlug={slug}
        onClick={() => handleItemClick(item)}
        className={classNames('showMoreNavItem d-flex flex-row align-middle py-3', {
          activeNav: item.slug === slug
        })}
        iconClassName="showMoreNavItemIcon pl-1"
        textClassName="showMoreNavItemText"
        iconSize="2x"
        useLongText
        {...(item.iconStyles ? { iconStyles: item.iconStyles } : {})}
      />
    ),
    [handleItemClick, slug]
  );

  const renderPrimaryNavItem = useCallback(
    (item, index) => {
      if (index > MORE_TRIGGER_INDEX && isMobile) return null;

      if (index === MORE_TRIGGER_INDEX && isMobile) {
        return (
          <NavItemRow
            onClick={handleMore}
            item={item}
            currentSlug={slug}
            className="productSideBarContainerNavItem font-weight-bold d-flex flex-column flex-md-row py-3 py-lg-4 my-lg-0"
            key={getNavItemKey(item, index, 'sidebar-more')}
            icon={faList}
            label="Discover More"
          />
        );
      }

      return (
        <NavItemRow
          key={getNavItemKey(item, index, 'sidebar')}
          item={item}
          currentSlug={slug}
          onClick={() => handleItemClick(item)}
          className={classNames(
            'd-flex flex-column flex-md-row py-3 py-lg-4 my-lg-0',
            item.inactive === undefined ? 'productSideBarContainerNavItem' : 'ml-4',
            item.customClass,
            { activeNav: item.slug === slug }
          )}
          iconClassName="pt-0"
          textClassName="pl-md-4 font-weight-bold"
          withImageSpacing
          useLongText={!isMobile}
          {...(item.iconStyles ? { iconStyles: item.iconStyles } : {})}
        />
      );
    },
    [handleItemClick, handleMore, isMobile, slug]
  );

  const isCheckout = path[1] === 'checkout';
  const isLanding = path[1] === 'landing';

  if (isCheckout || isLanding) {
    return null;
  }

  return (
    <div>
      {/* SHOW MORE NAV ON LEFT SIDE */}
      <div
        className={classNames('d-print-none', showMoreNav, showMoreNavClass, hideNav ? 'd-none' : '')}
        role="navigation"
      >
        <button type="button" onClick={handleMore} className="closeMark" aria-label="Close navigation">
          X
        </button>
        {navItems.map(renderExpandedNavItem)}
        {renderViewAllButton({ size: 'lg', block: true })}
      </div>
      {/* END SHOW MORE NAV ON LEFT SIDE */}

      {/* NORMAL NAVIGATION  LEFT OR BOTTOM BAR */}
      <div
        className={classNames('d-print-none', productSideBarContainer, visibilityClass, {
          [pptStyles]: specialStylesProp
        })}
        role="navigation"
      >
        {navItems.map(renderPrimaryNavItem)}
        {renderViewAllButton({ wrapperClassName: 'align-items-center d-none d-lg-block' })}
      </div>
      <div className={classNames(productSidebarChildren)} style={hideNav ? { paddingLeft: 0 } : {}}>
        {showDisclaimer && disclaimerRequired ? (
          <Container>
            <Row>
              <Col>
                <Disclaimer contentId={contentId} productInPath={thisProductInPath} />
                <Button onClick={handleDisclaimer}>Agree</Button>
              </Col>
            </Row>
          </Container>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

SideBar.propTypes = {
  children: PropTypes.node,
  contentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disclaimerRequired: PropTypes.bool,
  handleNav: PropTypes.func.isRequired,
  navItems: PropTypes.arrayOf(PropTypes.object),
  specialStyles: PropTypes.string
};

export default SideBar;
